import {
  Button,
  Card,
  CardActionArea,
  GridLegacy as Grid,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import { evolve, filter, map, pipe, propOr, sort } from "ramda";
import { useContext } from "react";
import { Msg } from "../../components/Msg";
import { useMsg } from "../../components/Msg/Msg";
import { P } from "../../components/Typography";
import { generalMessages } from "../../components/messages";
import { routes } from "../../routes";
import { renameKeys } from "../../utils/helpers";
import { useAuth } from "../Authorization";
import { useUpcomingCoachSessionsQuery } from "../Clients/api";
import { useUserUpcomingSessionsQuery } from "../Coaches/api";
import { I18nContext } from "../I18n/I18nProvider";
import { Loaders, QueryRenderer } from "../QM/QueryRenderer";
import { ActionStepsReadOnly } from "../Sessions/Sessions";
import { useUserSessionQuery } from "../Sessions/api";
import { AiResponseAccordion } from "./AiResponseAccordion";
import { DashboardBlockTitle } from "./DashboardBlockTitle";
import { actionBlueprintKey, anyTruthy } from "./DashboardRightMenu";
import { EmptyActionCardContent } from "./EmptyActionCardContent";
import { UpcomingSessions } from "./UpcomingSessions";
import { dashboardMessages } from "./messages";

const UpcomingSessionsContent = () => {
  const { isCoach, user } = useAuth();
  const { i18n } = useContext(I18nContext);
  const msg = useMsg({ dict: dashboardMessages });
  const generalMsg = useMsg({ dict: generalMessages });
  const coachUpcomingSessionsQuery = useUpcomingCoachSessionsQuery({
    enabled: isCoach,
  });
  const userUpcomingSessionsQuery = useUserUpcomingSessionsQuery({
    enabled: !isCoach,
  });
  const query = isCoach
    ? coachUpcomingSessionsQuery
    : userUpcomingSessionsQuery;

  const empty = (
    <EmptyActionCardContent
      iconName="CalendarMonth"
      title={<Msg id="dashboard.upcoming.empty.title" />}
      extra_bak={
        // TODO: do we want it or was extra intentionally removed? (select coach + start private session)
        <Stack
          useFlexGap
          divider={
            <P sx={{ textAlign: "center", my: 1 }}>
              {generalMsg("general.or")}
            </P>
          }
        >
          {pipe(
            filter(propOr(true, "visible")),
            map((buttonDef) => (
              <Button key={buttonDef.key || buttonDef.href} {...buttonDef} />
            )),
          )([
            {
              visible: !isCoach,
              variant: "outlined",
              href: routes.coaches,
              children: user.data.coach
                ? msg("dashboard.upcoming.empty.action.schedule-session")
                : msg("dashboard.upcoming.empty.action.select-coach"),
            },
            {
              visible: true,
              variant: "outlined",
              href: routes.startSession,
              children: msg("dashboard.actions.empty.action"),
            },
          ])}
        </Stack>
      }
    />
  );

  return (
    <QueryRenderer
      query={query}
      loaderEl={<Loaders.CircularBlock fullHeight />}
      errored={null}
      success={({ data }) => {
        if (!data?.length) return empty;

        const parseTime = evolve({ time: i18n.parseUTCLocal });
        const mappedData = pipe(
          map(
            isCoach
              ? parseTime
              : pipe(renameKeys({ coach: "username" }), parseTime),
          ),
          sort((a, b) => +a.time - b.time),
        )(data);
        return (
          <CardActionArea
            sx={{ height: "100%" }}
            href={isCoach ? routes.clients : routes.mySessions}
          >
            <UpcomingSessions data={mappedData} />
          </CardActionArea>
        );
      }}
    />
  );
};

// const MOCK = [
//   { checked: false, date: "2023-09-06", id: 52, label: "Mock Task1: TODO: RM from FE", },
//   { checked: true, date: "2023-09-06", id: 42, label: "Mock Task2: TODO: RM from FE", },
// ];

const ActionsContent = ({ insightsQuery }) => {
  const msg = useMsg({ dict: dashboardMessages });
  const sessionQuery = useUserSessionQuery({
    enabled: true,
    refetchOnReconnect: true,
  });

  const empty = (
    <EmptyActionCardContent
      iconName="RocketLaunch"
      title={<Msg id="dashboard.actions.empty.title" />}
      perex={<Msg id="dashboard.actions.empty.perex" />}
      button={{
        variant: "outlined",
        href: routes.startSession,
        children: <Msg id="dashboard.actions.empty.action" />,
      }}
    />
  );

  return (
    <QueryRenderer
      query={sessionQuery}
      loaderEl={<Loaders.CircularBlock fullHeight />}
      success={({ data: { actionSteps = [] } }) => {
        if (!actionSteps?.length) return empty;
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "space-between",
              gap: 3,
              p: 3,
            }}
          >
            <ActionStepsReadOnly steps={actionSteps} />
            <QueryRenderer
              query={insightsQuery}
              loading={null}
              errored={null}
              success={({ data }) => {
                return (
                  <AiResponseAccordion
                    isPending={anyTruthy(
                      "isPending",
                      [actionBlueprintKey],
                      data,
                    )}
                    summary={msg(
                      "dashboard.cards.ai.tips.personal-growth.title",
                    )}
                    text={data[actionBlueprintKey]?.text}
                    hideEmpty
                    markdown
                  />
                );
              }}
            />
          </Box>
        );
      }}
    />
  );
};

const sessionCardSx = { display: "flex", gap: 3, flexDirection: "column" };

export const UpcomingAndActions = ({ insightsQuery }) => {
  const dashboardMsg = useMsg({ dict: dashboardMessages });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={5} xl={4} sx={sessionCardSx}>
        <DashboardBlockTitle iconName={"CalendarMonth"}>
          {dashboardMsg("dashboard.upcoming.heading")}
        </DashboardBlockTitle>
        <Card sx={{ minHeight: "150px" }}>
          <UpcomingSessionsContent />
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} lg={7} xl={8} sx={sessionCardSx}>
        <DashboardBlockTitle iconName={"RocketLaunch"}>
          {dashboardMsg("dashboard.rightmenu.actions.heading")}
        </DashboardBlockTitle>
        <Card sx={{ minHeight: "150px" }}>
          <ActionsContent insightsQuery={insightsQuery} />
        </Card>
      </Grid>
    </Grid>
  );
};
